import React, { useState, useEffect } from 'react';
import { authAPI } from '../../services/api';
import { Shield, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';

interface CompanyRole {
  id: string;
  name: string;
  description?: string;
  permissions?: Array<Record<string, unknown>>;
  parent_role_id?: string;
  is_system_role: boolean;
}

interface CompanyRolesManagerProps {
  companyId: string;
}

const CompanyRolesManager: React.FC<CompanyRolesManagerProps> = ({
  companyId,
}) => {
  const { t } = useI18n();
  const [roles, setRoles] = useState<CompanyRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    permissionsText: '',
    parent_role_id: '',
  });

  const loadRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.get(`/companies/${companyId}/roles`);
      setRoles(response.data || []);
    } catch (err: any) {
      console.error('[CompanyRolesManager] Failed to load roles', err);
      setError(t('rbac.roles.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, [companyId]);

  const resetForm = () => {
    setForm({ name: '', description: '', permissionsText: '', parent_role_id: '' });
    setEditingId(null);
    setError(null);
    setMessage(null);
  };

  const handleEdit = (role: CompanyRole) => {
    setForm({
      name: role.name,
      description: role.description || '',
      permissionsText: role.permissions
        ? JSON.stringify(role.permissions, null, 2)
        : '[]',
      parent_role_id: role.parent_role_id || '',
    });
    setEditingId(role.id);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      let permissions: Array<Record<string, unknown>> = [];
      if (form.permissionsText.trim()) {
        permissions = JSON.parse(form.permissionsText);
      }

      const payload: any = {
        name: form.name,
        description: form.description || undefined,
        permissions,
      };

      if (form.parent_role_id) {
        payload.parent_role_id = form.parent_role_id;
      }

      await authAPI.put(`/admin/companies/${companyId}/roles`, payload);

      setMessage('Role salvo com sucesso!');
      resetForm();
      await loadRoles();
    } catch (err: any) {
      console.error('[CompanyRolesManager] Failed to save role', err);
      setError(err.response?.data?.error || 'Não foi possível salvar role.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (roleName: string) => {
    if (!confirm('Tem certeza que deseja excluir este role?')) {
      return;
    }

    try {
      await authAPI.delete(`/admin/companies/${companyId}/roles/${roleName}`);
      setMessage('Role excluído com sucesso!');
      await loadRoles();
    } catch (err: any) {
      console.error('[CompanyRolesManager] Failed to delete role', err);
      setError(err.response?.data?.error || 'Não foi possível excluir role.');
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>{t('common.loading')}</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>{t('rbac.roles.title')}</h3>
        {!editingId && (
          <button
            onClick={() => setEditingId('new')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <Plus size={16} />
            Novo Role
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {message && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          {message}
        </div>
      )}

      {editingId && (
        <form onSubmit={handleSave} style={{ marginBottom: '24px', padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <h4 style={{ marginTop: 0 }}>{editingId === 'new' ? 'Novo Role' : 'Editar Role'}</h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Nome
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
                placeholder="manager, supervisor, etc"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Role Pai (opcional)
              </label>
              <select
                value={form.parent_role_id}
                onChange={(e) => setForm({ ...form, parent_role_id: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="">{t('common.none')}</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              Descrição
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
              placeholder={t('rbac.roles.descPlaceholder')}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              Permissões (JSON array)
            </label>
            <textarea
              value={form.permissionsText}
              onChange={(e) => setForm({ ...form, permissionsText: e.target.value })}
              rows={6}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'monospace',
              }}
              placeholder='[{"resource": "clients", "action": "read"}, {"resource": "clients", "action": "create"}]'
            />
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: saving ? 0.6 : 1,
              }}
            >
              <Save size={16} />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#6b7280',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <X size={16} />{t('actions.cancel')}</button>
          </div>
        </form>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('common.name')}</th>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('common.description')}</th>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('common.type')}</th>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('rbac.roles.permissions')}</th>
              <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {roles.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>{t('rbac.roles.noneConfigured')}</td>
              </tr>
            ) : (
              roles.map((role) => (
                <tr key={role.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{role.name}</td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>{role.description || '-'}</td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {role.is_system_role ? (
                      <span style={{ color: '#059669', fontSize: '12px' }}>Sistema</span>
                    ) : (
                      <span style={{ color: '#2563eb', fontSize: '12px' }}>{t('rbac.roles.custom')}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {role.permissions && role.permissions.length > 0
                      ? `${role.permissions.length} permissão(ões)`
                      : '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    {!role.is_system_role && (
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleEdit(role)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                          title={t('actions.edit')}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(role.name)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#fee2e2',
                            border: '1px solid #fecaca',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                          title={t('common.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyRolesManager;

